import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";

import { authResponseSchema } from "@opendialysis/procare-api-schemas";

import { env } from "../config/env";
import { API } from "../constants/apiRoutes";
import { getAccessToken, setTokens } from "./session";

declare module "axios" {
  export interface AxiosRequestConfig {
    /**
     * Suppresses the Authorization header. The /activate and /reset-password
     * pages must reach PUT /users/password WITHOUT a bearer: the route branches
     * on the header's presence and would otherwise change the signed-in user's
     * password instead of honouring the emailed token.
     */
    skipAuth?: boolean;
    /** Internal: marks a request already retried after a refresh. */
    _retry?: boolean;
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  // axios has no default timeout; without one a hung request freezes a screen
  // with no way out.
  timeout: 15_000,
  // Required when the dashboard is served cross-origin from the API. Harmless
  // same-origin, and it keeps the CORS deployment a config-only switch.
  withCredentials: true,
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // The gate is optional on the API; send the key only when this build has one.
  if (env.VITE_API_KEY) config.headers.set("x-api-key", env.VITE_API_KEY);

  const token = getAccessToken();
  if (token && !config.skipAuth) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  return config;
});

/**
 * Single-flight guard: concurrent 401s share ONE refresh request.
 *
 * Refresh rotation is delete-on-use on the server (it issues a new token, then
 * deletes the old row), so a second parallel refresh consumes an
 * already-rotated token, fails, and forces a logout. Mirrors
 * apps/mobile/src/network/interceptors/responseInterceptor.ts.
 */
let refreshPromise: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  const run = async (): Promise<string | null> => {
    try {
      // skipAuth: the expired bearer is worthless here and the cookie is what
      // authenticates this call.
      const { data } = await apiClient.post(API.auth.refresh, undefined, {
        skipAuth: true,
      });
      const parsed = authResponseSchema.parse(data);
      setTokens(parsed.token, parsed.tokenExpireAt);
      return parsed.token;
    } catch {
      return null;
    }
  };

  // Cross-TAB guard on top of the in-module one. Two tabs reloading at the
  // same moment would otherwise both refresh, and delete-on-use rotation
  // would sign one of them out. The loser of the lock re-reads the cookie jar,
  // which the winner has already updated.
  if (typeof navigator !== "undefined" && navigator.locks) {
    return navigator.locks.request("procare-dashboard-refresh", run);
  }
  return run();
}

export function refreshAccessToken(): Promise<string | null> {
  refreshPromise ??= performRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

/**
 * Wired in by the app shell so this module does not import the query client or
 * i18n (both would create cycles through apiError/session).
 */
type ForcedLogoutHandler = () => void;
let onForcedLogout: ForcedLogoutHandler = () => {};
export function setForcedLogoutHandler(handler: ForcedLogoutHandler) {
  onForcedLogout = handler;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;
    if (!original) return Promise.reject(error);

    // Never refresh the refresh call itself — that loops forever.
    const isRefreshCall = Boolean(original.url?.includes(API.auth.refresh));

    if (
      error?.response?.status !== 401 ||
      original._retry ||
      isRefreshCall ||
      // A 401 on an explicitly unauthenticated call (password reset) is a real
      // answer, not an expired session.
      original.skipAuth
    ) {
      return Promise.reject(error);
    }

    original._retry = true;
    const token = await refreshAccessToken();

    if (token) {
      original.headers.set("Authorization", `Bearer ${token}`);
      return apiClient(original);
    }

    // Refresh impossible: end the session exactly as a sign-out would.
    onForcedLogout();
    return Promise.reject(error);
  },
);

/**
 * NOT doing proactive/timer-based refresh, deliberately. `tokenExpireAt` is
 * available and refreshing at T-30s is tempting, but a timer firing while a
 * 401-triggered refresh is in flight is precisely the two-concurrent-refresh
 * case that delete-on-use rotation punishes. Reactive only, one path, one guard.
 */
